import { useTranslation } from '@mukil/i18n';
import { Badge, EmptyState, GlassCard } from '@jumpstone/ui-react';
import type { ExpiringDocument } from '../../data/types';

const RENEWAL_SOON_DAYS = 30;

/**
 * Documents and certifications approaching their renewal date.
 *
 * No document-expiry concept exists anywhere in the schema yet (C5,
 * #21936) — `documents` is always `[]` today (see `mock_data.ts`); this
 * renders an `EmptyState` rather than either a blank card or fabricated
 * data, pending a product decision on document-expiry tracking.
 */
export function ExpiringDocumentsCard({
  documents,
}: {
  documents: ExpiringDocument[];
}): React.JSX.Element {
  const { t } = useTranslation('wp');

  if (documents.length === 0) {
    return (
      <GlassCard className="rounded-2xl p-5">
        <h2 className="text-[14.5px] font-bold mb-2.5">
          {t('dashboard.expiring_documents')}
        </h2>
        <EmptyState
          title={t('dashboard.expiring_documents.empty_title')}
          description={t('dashboard.expiring_documents.empty_description')}
        />
      </GlassCard>
    );
  }

  return (
    <GlassCard className="rounded-2xl p-5">
      <h2 className="text-[14.5px] font-bold mb-2.5">
        {t('dashboard.expiring_documents')}
      </h2>
      {documents.map((document) => (
        <div key={document.id} className="wp-listrow text-[13px]">
          <span>
            {document.employee_name === null
              ? document.document_name
              : t('dashboard.document_owner', {
                  name: document.employee_name,
                  document: document.document_name,
                })}
          </span>
          {document.days_remaining === null ? (
            <Badge variant="neutral">{t('dashboard.renewed')}</Badge>
          ) : (
            <Badge
              variant={
                document.days_remaining <= RENEWAL_SOON_DAYS
                  ? 'warning'
                  : 'info'
              }
              className="num"
            >
              {t('dashboard.days_remaining', {
                days: document.days_remaining,
              })}
            </Badge>
          )}
        </div>
      ))}
    </GlassCard>
  );
}
